import React, { useState, useEffect, useContext } from "react";
import { keyBy, cloneDeep, groupBy } from "lodash";
import * as Tabs from "@radix-ui/react-tabs";
import { gql, useMutation, useQuery } from "urql";
import { Link, useLocation, useParams, useSearchParams } from "wouter";
import { RotateCcw } from "lucide-react";
import { Button } from "../../../components/src/Button";

import { Panel } from "../../../components/src/common/Panel";
import { usePrevious } from "../../../components/src/common/Utils";
import { DateForm } from "../../../components/src/dateForm";
import { emptyObjectId } from "../../../common/src";

import { MiniTransactionForm, miniTransactionTypes, NewMiniTransactionButton } from "./MiniTransactionForm";
import { ITransactionDataRow } from "../components/TransactionsGrid";
import { useQueryState } from "../../../components/src/use-query-state";
import { serializeSwedenDate } from "../../../components/src/dateFormater";
import {
    BankAccountBalance,
    BankAccountTransaction,
    BankAccountTransactionInput,
    BankAccountTransactionStatusEnum,
    InstrumentProductTypeEnum,
    MatchBankAccountTransactionsInput,
    Party,
    PartyType,
    PermissionAssetEnum,
    Position,
    Transaction,
    TransactionItem,
    TransactionItemType,
    TransactionStatus,
    UpdateTransactionInput
} from "../types.generated";
import { ClientContextSelector } from "../contexts/ClientContextSelector";
import { ClientContext, IClient } from "../../../components/src/contexts/ClientContext";
import { BankAccountReconciliationGrid } from "./BankAccountsReconciliationGrid";
import { GET_ME } from "../common/queries";
import { ResponsiveTabsList } from "../../../components/src/ResponsiveTabsList";

const GET_TRANSACTIONS = gql`
    query transactions($filter: TransactionFilterInput) {
        transactions(filter: $filter) {
            _id
            type
            status
            tradeDate
            items {
                _id
                transactionId
                instrumentId
                instrument {
                    name
                    currency
                    productType
                }
                type
                instrumentId
                error
                valueDate
                amount
                transactionStatus
                transactionTradeDate
                transactionType
            }
        }
    }
`;

const GET_BANK_ACCOUNT_TRANSACTIONS = gql`
    query bankAccountTransactions($filter: BankAccountTransactionFilterInput) {
        bankAccountTransactions(filter: $filter) {
            _id
            clientId
            client {
                name
            }
            instrumentId
            instrument {
                _id
                name
                bic
                iban
                currency
                productType
            }
            date
            error
            externalId
            amount
            status
            reference
            description
            additionalInformation
            createTimestamp
            updateTimestamp
            updateUserId
            updateUserInfo {
                name
            }
        }
    }
`;

const GET_SETTLED_POSITION_BALANCE = gql`
    query {
        positions(filter: { statusIn: [Settled] }, groupPositionsBy: ClientId) {
            amount
            instrumentId
        }
    }
`;

const GET_SETTLED_BANK_BALANCE = gql`
    query bankAccountBalances($filter: BankAccountBalanceFilterInput) {
        bankAccountBalances(filter: $filter) {
            balance
            instrumentId
        }
    }
`;

const GET_CLIENTS = gql`
    query {
        clients: parties(filter: { typeIn: [Fund, Client] }) {
            _id
            name
            instruments {
                _id
                name
                iban
            }
        }
    }
`;

const UPDATE_TRANSACTIONS = gql`
    mutation updateTransactions($input: [UpdateTransactionInput!]!) {
        updateTransactions(input: $input) {
            _id
        }
    }
`;

const UPSERT_BANK_ACCOUNT_TRANSACTIONS = gql`
    mutation upsertBankAccountTransactions($input: [BankAccountTransactionInput!]!) {
        upsertBankAccountTransactions(input: $input) {
            _id
        }
    }
`;

const MATCH_BANK_ACCOUNT_TRANSACTIONS = gql`
    mutation manuallyMatchBankAccountTransactions($input: [MatchBankAccountTransactionsInput!]!) {
        manuallyMatchBankAccountTransactions(input: $input) {
            transactionItemIds
            bankAccountTransactionIds
        }
    }
`;

const defaultViewFromDateString = () => {
    return serializeSwedenDate(new Date(new Date().setDate(new Date().getDate() - 30)));
};

const transactionGroups = ["Not settled - future", "Not settled", "Settled"];

const groupByStatus = (transactionItems: BankAccountTransaction[] | TransactionItem[], transactionGroups: string[]) => {
    const result: Record<string, BankAccountTransaction[] | TransactionItem[]> = {};
    transactionGroups.forEach((status) => {
        result[status] = [];
    });

    if (transactionItems.length) {
        transactionItems.forEach((item) => {
            if (item.transactionStatus === TransactionStatus.Settled || item.status === BankAccountTransactionStatusEnum.Settled) {
                item.statusGroup = item.transactionStatus ? item.transactionStatus : item.status;
            } else {
                const today = new Date().toISOString().slice(0, 10);
                if (item.date > today || item.valueDate > today) {
                    item.statusGroup = "Not settled - future";
                } else {
                    item.statusGroup = "Not settled";
                }
            }
            if (transactionGroups.includes(item.statusGroup)) {
                result[item.statusGroup].push(item);
            }
        });
    }

    return result;
};

export const BankAccountReconciliationPage = (): React.ReactElement => {
    const [, navigate] = useLocation();
    const [queryArgs] = useSearchParams();

    const { client } = useContext(ClientContext);
    const previousClient: IClient = usePrevious(client);
    const [viewFromDate] = useQueryState("viewFromDate", defaultViewFromDateString());
    const [tab, setTab] = useState(null);
    const [instrumentIds, setInstrumentIds] = useState([emptyObjectId]);
    const [spinner, setSpinner] = useState(false);
    const [{ fetching: loadingClients, error: errorClients, data: dataClients }] = useQuery<{ clients: Partial<Party>[] }>({
        query: GET_CLIENTS,
        requestPolicy: "cache-and-network"
    });
    const [{ fetching: loadingMe, error: errorMe, data: dataMe }] = useQuery({
        query: GET_ME,
        requestPolicy: "cache-and-network"
    });

    const [{ fetching: loadingUpdateTransactionsStatus, error: errorUpdate }, updateTransactionsStatus] = useMutation(UPDATE_TRANSACTIONS);
    const [{ error: errorUpsertBankAccountTransactions }, updateBankAccountTransactionsStatus] =
        useMutation(UPSERT_BANK_ACCOUNT_TRANSACTIONS);
    const [{ fetching: loadingMatchBankAccountTransactions, error: errorMatchBankAccountTransactions }, matchBankAccountTransactions] =
        useMutation(MATCH_BANK_ACCOUNT_TRANSACTIONS);

    const params = useParams<{ id: string }>();
    let id = params.id;

    let type = miniTransactionTypes[0];

    if (id && id.startsWith("new")) {
        type = miniTransactionTypes.find((d) => d.toLowerCase() === id.substring(3, id.length));
        id = "new";
    }

    const [{ fetching: loadingTransactions, error: errorTransactions, data: dataTransactions }, refetchTransactions] = useQuery({
        query: GET_TRANSACTIONS,
        variables: {
            filter: {
                startDate: viewFromDate || defaultViewFromDateString(),
                instrumentIdsIn: instrumentIds
            }
        },
        requestPolicy: "network-only"
    });

    const [{ fetching: loadingPositionBalance, error: errorPositionBalance, data: dataPositionBalances }, refetchPositionBalances] =
        useQuery({
            query: GET_SETTLED_POSITION_BALANCE,
            variables: {},
            requestPolicy: "network-only"
        });

    const [
        { fetching: loadingBankAccountTransactions, error: errorBankAccountTransactions, data: dataBankAccountTransactions },
        refetchBankAccountTransactions
    ] = useQuery({
        query: GET_BANK_ACCOUNT_TRANSACTIONS,
        variables: {
            filter: {
                startDate: viewFromDate || defaultViewFromDateString(),
                instrumentIdIn: instrumentIds,
                statusIn: [BankAccountTransactionStatusEnum.Pending, BankAccountTransactionStatusEnum.Settled]
            }
        },
        requestPolicy: "network-only"
    });

    const [{ fetching: loadingBankBalance, error: errorBankBalance, data: dataBankBalances }, refetchBankBalances] = useQuery({
        query: GET_SETTLED_BANK_BALANCE,
        variables: { filter: { instrumentIdIn: instrumentIds, statusIn: [BankAccountTransactionStatusEnum.Settled] } },
        requestPolicy: "network-only"
    });

    useEffect(() => {
        if (dataClients) {
            if (client) {
                if (
                    (client && instrumentIds.length === 1 && instrumentIds[0] === emptyObjectId) ||
                    (client && previousClient && client._id !== previousClient._id)
                ) {
                    for (let i = 0; i < dataClients.clients.length; i++) {
                        const clientData = dataClients.clients[i];
                        if (client._id === clientData._id) {
                            setInstrumentIds(clientData.instruments.map((instrument) => instrument._id));
                            break;
                        }
                    }
                }
            }
        }
        /*if (client && client._id === emptyObjectId && Object.keys(queryArgs).length) {
            navigate("/reconcile/bankaccounts/" + client.dashName);
        }*/
    }, [dataClients, client, instrumentIds, previousClient, queryArgs, navigate]);

    useEffect(() => {
        setSpinner(false);
    }, [client, viewFromDate, tab]);

    if (loadingClients || loadingMe) return <p>Loading...</p>;
    if (errorClients) return <p>errorClients: {JSON.stringify(errorClients, null, 2)}</p>;
    if (errorMe) return <p>errorUser: {JSON.stringify(errorMe, null, 2)}</p>;
    if (errorUpdate) return <p>error: {JSON.stringify(errorUpdate, null, 2)}</p>;
    if (errorUpsertBankAccountTransactions) return <p>error: {JSON.stringify(errorUpsertBankAccountTransactions, null, 2)}</p>;
    if (errorMatchBankAccountTransactions) return <p>error: {JSON.stringify(errorMatchBankAccountTransactions, null, 2)}</p>;

    if (errorTransactions) return <p>errorTransactionItems: {JSON.stringify(errorTransactions, null, 2)}</p>;

    if (errorBankAccountTransactions) return <p>errorBankAccountTransactions: {JSON.stringify(errorBankAccountTransactions, null, 2)}</p>;

    if (errorPositionBalance) return <p>errorPosition: {JSON.stringify(errorPositionBalance, null, 2)}</p>;

    if (errorBankBalance) return <p>errorBankAccountBalance {JSON.stringify(errorBankBalance, null, 2)}</p>;

    if (!dataTransactions || !dataBankAccountTransactions || !dataPositionBalances || !dataBankBalances) {
        return <div>Loading cash balance</div>;
    }

    type ClientInstrumentForTabs = { _id: string; name: string; iban?: string | null };
    type ClientForTabs = { _id: string; instruments: ClientInstrumentForTabs[] };

    const clientInstruments = (dataClients.clients as ClientForTabs[]).find((x: ClientForTabs) => x._id === client._id)?.instruments;
    const tabs: Record<string, string> = clientInstruments
        ? Object.assign(
              {},
              ...clientInstruments
                  .filter((x: ClientInstrumentForTabs) => Boolean(x.iban && x.iban !== ""))
                  .map((x: ClientInstrumentForTabs) => ({ [x._id]: x.name }))
          )
        : {};

    const transactionsClone: Transaction[] = cloneDeep(dataTransactions.transactions);
    const positionBalancesClone: Position[] = cloneDeep(dataPositionBalances.positions);
    const bankAccountTransactionsClone: BankAccountTransaction[] = cloneDeep(dataBankAccountTransactions.bankAccountTransactions);
    const bankBalancesClone: BankAccountBalance[] = cloneDeep(dataBankBalances.bankAccountBalances);

    let transactionItems: TransactionItem[] = [];
    const transactionsByItemId: Record<string, Partial<Transaction>> = {};
    for (const transaction of transactionsClone) {
        for (const item of transaction.items) {
            //Only interested in cashAccounts
            if (
                item.type === TransactionItemType.SettlementAmount &&
                item.instrument.productType === InstrumentProductTypeEnum.CashAccount
            ) {
                transactionItems.push(item);
                transactionsByItemId[item._id] = transaction;
            }
        }
    }

    transactionItems = transactionItems.sort((a, b) => {
        return new Date(b.valueDate).getTime() - new Date(a.valueDate).getTime();
    });

    const groupedByInstrumentId = groupBy(transactionItems, "instrumentId");
    const groupedPositionsByInstrumentId = keyBy(positionBalancesClone, "instrumentId");

    const groupedBankAccountTransactionsByInstrumentId = groupBy(bankAccountTransactionsClone, "instrumentId");
    const groupedBankBalancesByInstrumentId = keyBy(bankBalancesClone, "instrumentId");

    if (groupedBankAccountTransactionsByInstrumentId && Object.keys(groupedBankAccountTransactionsByInstrumentId)) {
        Object.keys(groupedBankAccountTransactionsByInstrumentId).forEach((instrumentId) => {
            //Only interested in cashAccounts
            if (
                !tabs[instrumentId] &&
                groupedBankAccountTransactionsByInstrumentId[instrumentId][0].instrument.productType ===
                    InstrumentProductTypeEnum.CashAccount
            ) {
                tabs[instrumentId] = groupedBankAccountTransactionsByInstrumentId[instrumentId][0].instrument.name;
            }
        });
    }

    const tabNames: string[] = [];
    for (const key in tabs) {
        tabNames.push(tabs[key]);
    }
    const tabNamesSorted = tabNames.sort();
    const tabsSortedByName: Record<string, string> = {};
    tabNamesSorted.forEach((instrumentName) => {
        for (const tabKey in tabs) {
            if (tabs[tabKey] === instrumentName) {
                tabsSortedByName[tabKey] = instrumentName;
            }
        }
    });

    let activeTab: string = null;
    if (tab && tabsSortedByName[tab]) {
        activeTab = tab;
    } else if (Object.keys(tabsSortedByName).length > 0) {
        activeTab = Object.keys(tabsSortedByName)[0];
    }

    let groups: Record<string, TransactionItem[]> = {};
    if (Object.keys(groupedByInstrumentId).length > 0 && groupedByInstrumentId[activeTab]) {
        groups = groupByStatus(groupedByInstrumentId[activeTab], transactionGroups) as unknown as Record<string, TransactionItem[]>;
    }

    let bankAccountGroups: Record<string, BankAccountTransaction[]> = {};
    if (Object.keys(groupedBankAccountTransactionsByInstrumentId).length > 0 && groupedBankAccountTransactionsByInstrumentId[activeTab]) {
        bankAccountGroups = groupByStatus(groupedBankAccountTransactionsByInstrumentId[activeTab], transactionGroups) as unknown as Record<
            string,
            BankAccountTransaction[]
        >;
    }

    const handleUpdate = async (input: UpdateTransactionInput[]) => {
        setSpinner(true);
        await updateTransactionsStatus({ input: input })
            .then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetchTransactions();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleBankAccountTransactionStatusUpdate = async (inputBase: Partial<BankAccountTransactionInput>[]) => {
        setSpinner(true);
        const input = bankAccountTransactionsUpdateFormat(inputBase);
        await updateBankAccountTransactionsStatus({ input: input })
            .then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetchBankAccountTransactions();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const handleBankAccountTransactionsMatch = async (input: MatchBankAccountTransactionsInput[]) => {
        setSpinner(true);
        await matchBankAccountTransactions({ input: input })
            .then((result) => {
                if (result.error) {
                    console.error(result.error.toString());
                } else {
                    refetchTransactions();
                    refetchBankAccountTransactions();
                }
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const formatTransactionItems = (
        itemsGrouped: Record<string, TransactionItem[]>,
        instrumentId: string,
        instrumentName: string,
        currency: string
    ): Record<string, ITransactionDataRow[]> => {
        const formattedDataByGroup = {};
        if (itemsGrouped && Object.keys(itemsGrouped)) {
            for (const group of Object.keys(itemsGrouped)) {
                const items = itemsGrouped[group];
                if (items) {
                    formattedDataByGroup[group] = items.map((item: any) => {
                        return {
                            _id: item.transactionId,
                            amount: item.amount,
                            currency: currency,
                            instrumentId: instrumentId,
                            instrumentName: instrumentName,
                            tradeDate: item.transactionTradeDate,
                            valueDate: item.valueDate,
                            type: item.transactionType,
                            status: item.transactionStatus,
                            transactionItemId: item._id,
                            error: item.error
                        };
                    });
                }
            }
        }
        return formattedDataByGroup;
    };

    const formatBankAccountTransactions = (
        itemsGrouped: Record<string, BankAccountTransaction[]>,
        instrumentId: string,
        instrumentName: string,
        currency: string
    ): Record<string, ITransactionDataRow[]> => {
        const formattedDataByGroup = {};
        if (itemsGrouped && Object.keys(itemsGrouped)) {
            for (const group of Object.keys(itemsGrouped)) {
                const items = itemsGrouped[group];
                if (items) {
                    formattedDataByGroup[group] = items.map((item: any) => {
                        return {
                            _id: item._id,
                            amount: parseFloat(item.amount),
                            currency: currency,
                            instrumentId: instrumentId,
                            instrumentName: instrumentName,
                            valueDate: item.date,
                            description: item.description,
                            reference: item.reference,
                            status: item.status,
                            error: item.error,
                            updatedByUserName: item.updateUserInfo ? item.updateUserInfo.name : ""
                        };
                    });
                }
            }
        }
        return formattedDataByGroup;
    };

    const bankAccountTransactionsUpdateFormat = (updateInput: Partial<BankAccountTransactionInput>[]): BankAccountTransactionInput[] => {
        const bankAccountTransactionsById = keyBy(bankAccountTransactionsClone, "_id");
        const updateArray: BankAccountTransactionInput[] = [];
        for (const input of updateInput) {
            const bankAccountTransaction = bankAccountTransactionsById[input._id];

            updateArray.push({
                _id: input._id,
                clientId: bankAccountTransaction.clientId,
                instrumentId: bankAccountTransaction.instrumentId,
                date: bankAccountTransaction.date,
                externalId: bankAccountTransaction.externalId,
                amount: bankAccountTransaction.amount,
                status: input.status,
                reference: bankAccountTransaction.reference,
                description: bankAccountTransaction.description,
                additionalInformation: bankAccountTransaction.additionalInformation
            });
        }

        return updateArray;
    };

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full sm:w-1/2 md:w-4/12 px-2 mb-4">
                    <ClientContextSelector typeIn={[PartyType.Fund]} />
                </div>
                <div className="w-full sm:w-1/2 md:w-8/12 px-2 mb-4 text-right mr-3">
                    {dataMe &&
                    dataMe.me &&
                    dataMe.me.frontendRole &&
                    dataMe.me.frontendRole.assets &&
                    (dataMe.me.frontendRole.assets.includes(PermissionAssetEnum.BackOffice) ||
                        dataMe.me.frontendRole.assets.length === 0) ? (
                        <a
                            href="https://github.com/CaptorAB/documents/blob/master/BackOffice/manual/bank_account_reconciliation.md"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Manual
                        </a>
                    ) : null}
                </div>
            </div>

            {client && client._id !== emptyObjectId ? (
                <div>
                    <div className="flex flex-wrap -mx-2 mt-4">
                        <div className="w-full sm:w-1/2 md:w-4/12 px-2 mb-4">
                            <DateForm defaultDateString={viewFromDate} dateName={"viewFromDate"} className="width-fixed"></DateForm>
                        </div>
                        <div className="w-full sm:w-1/2 md:w-8/12 px-2 mb-4 text-right">
                            <NewMiniTransactionButton page={`reconcile/bankaccounts/${client.dashName}`} />
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-2 mb-2 mt-2">
                        <div className="w-full sm:w-1/2 md:w-4/12 px-2 mb-4">
                            <Button
                                variant={
                                    loadingTransactions || loadingBankAccountTransactions || loadingPositionBalance || loadingBankBalance
                                        ? "secondary"
                                        : "primary"
                                }
                                disabled={
                                    loadingTransactions || loadingBankAccountTransactions || loadingPositionBalance || loadingBankBalance
                                }
                                onClick={() => {
                                    refetchTransactions();
                                    refetchPositionBalances();
                                    refetchBankAccountTransactions();
                                    refetchBankBalances();
                                }}
                            >
                                <RotateCcw size={20} />
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full px-2 mb-4">
                            {activeTab && client && client._id ? (
                                <Tabs.Root value={activeTab} onValueChange={setTab} className="w-full">
                                    <ResponsiveTabsList value={activeTab} onValueChange={setTab}>
                                        {Object.keys(tabsSortedByName).map((instrumentId) => (
                                            <Tabs.Trigger
                                                key={instrumentId}
                                                value={instrumentId}
                                                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                            >
                                                {tabsSortedByName[instrumentId]}
                                            </Tabs.Trigger>
                                        ))}
                                    </ResponsiveTabsList>
                                    {Object.keys(tabsSortedByName).map((instrumentId) => {
                                        const instrumentName = groupedByInstrumentId[instrumentId]
                                            ? groupedByInstrumentId[instrumentId][0].instrument.name
                                            : groupedBankAccountTransactionsByInstrumentId[instrumentId]
                                              ? groupedBankAccountTransactionsByInstrumentId[instrumentId][0].instrument.name
                                              : null;
                                        const instrumentCurrency = groupedByInstrumentId[instrumentId]
                                            ? groupedByInstrumentId[instrumentId][0].instrument.currency
                                            : groupedBankAccountTransactionsByInstrumentId[instrumentId]
                                              ? groupedBankAccountTransactionsByInstrumentId[instrumentId][0].instrument.currency
                                              : null;

                                        return (
                                            <Tabs.Content key={instrumentId} value={instrumentId} className="mt-4">
                                                <Link to={"/parties/" + client._id + "/instruments/" + instrumentId}>Instrument</Link>

                                                <br />
                                                <div className={"flex flex-wrap -mx-2"}>
                                                    <div className={"w-full px-2 mb-4"}>
                                                        <BankAccountReconciliationGrid
                                                            key={`cb-${dataTransactions.transactions.length}`}
                                                            itemsByGroup={formatTransactionItems(
                                                                groups,
                                                                instrumentId,
                                                                instrumentName,
                                                                instrumentCurrency
                                                            )}
                                                            onUpdate={handleUpdate}
                                                            visibleColumns={[
                                                                "id",
                                                                "status",
                                                                "tradeDate",
                                                                "valueDate",
                                                                "type",
                                                                "amount",
                                                                "error"
                                                            ]}
                                                            header={"Transactions"}
                                                            itemsReconcileByGroup={formatBankAccountTransactions(
                                                                bankAccountGroups,
                                                                instrumentId,
                                                                instrumentName,
                                                                instrumentCurrency
                                                            )}
                                                            onUpdateReconcileStatus={handleBankAccountTransactionStatusUpdate}
                                                            onMatch={handleBankAccountTransactionsMatch}
                                                            visibleReconcileColumns={[
                                                                "description",
                                                                "reference",
                                                                "status",
                                                                "valueDate",
                                                                "amount",
                                                                "error"
                                                            ]}
                                                            headerReconcile={"Bank Account Transactions"}
                                                            headerGroups={transactionGroups}
                                                            spinner={
                                                                spinner &&
                                                                (loadingUpdateTransactionsStatus ||
                                                                    loadingMatchBankAccountTransactions ||
                                                                    loadingTransactions ||
                                                                    loadingBankAccountTransactions)
                                                            }
                                                            transactionsByItemId={transactionsByItemId}
                                                            instrumentId={instrumentId}
                                                            positionsByInstrumentId={groupedPositionsByInstrumentId}
                                                            bankBalancesByInstrumentId={groupedBankBalancesByInstrumentId}
                                                        />
                                                    </div>
                                                </div>
                                            </Tabs.Content>
                                        );
                                    })}
                                </Tabs.Root>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}
            <Panel
                isOpen={id != null}
                onDismiss={() => {
                    if (queryArgs.toString()) {
                        navigate(`/reconcile/bankaccounts/${client.dashName}?${queryArgs.toString()}`);
                    } else {
                        navigate(`/reconcile/bankaccounts/${client.dashName}`);
                    }
                }}
            >
                {id ? <MiniTransactionForm id={id === "new" ? null : id} type={id === "new" ? type : null} showHeader /> : null}
            </Panel>
        </div>
    );
};
